// src/auth/AuthProvider.jsx
import { createContext, useContext, useEffect, useMemo, useRef, useCallback, useState } from "react";
import { usePushNotifications } from "../lib/usePushNotifications";
import { getDemoAuthPayload as _getDemoAuthPayload } from "../demo/authPayload";
import api, {
  setAccessToken,
  clearAccessToken,
  logout as apiLogout,
  refreshAccessToken,
} from "../lib/api";

const AuthContext = createContext(null);
const isDemoMode = import.meta.env.VITE_DEMO_MODE === "true";
const LS_CURRENT_BUSINESS_ID = "current_business_id";
const LS_CURRENT_BUSINESS_SLUG = "current_business_slug";
const LS_CURRENT_LOCATION_ID = "current_location_id";
const LS_CURRENT_LOCATION_SCOPE_ID = "current_location_scope_id";

const getBusinessLocationIdKey = (businessId) =>
  `${LS_CURRENT_LOCATION_ID}:${String(businessId)}`;

const getBusinessLocationScopeKey = (businessId) =>
  `${LS_CURRENT_LOCATION_SCOPE_ID}:${String(businessId)}`;

const clearActiveLocationSelection = () => {
  localStorage.removeItem(LS_CURRENT_LOCATION_ID);
  localStorage.removeItem(LS_CURRENT_LOCATION_SCOPE_ID);
};

const persistLocationSelection = (businessId) => {
  if (!businessId) return;

  const locId = localStorage.getItem(LS_CURRENT_LOCATION_ID);
  const locScopeId = localStorage.getItem(LS_CURRENT_LOCATION_SCOPE_ID);

  if (locId) localStorage.setItem(getBusinessLocationIdKey(businessId), String(locId));
  else localStorage.removeItem(getBusinessLocationIdKey(businessId));

  if (locScopeId && locScopeId !== "all") {
    localStorage.setItem(getBusinessLocationScopeKey(businessId), String(locScopeId));
  } else {
    localStorage.removeItem(getBusinessLocationScopeKey(businessId));
  }
};

const restoreLocationSelection = (businessId) => {
  if (!businessId) {
    clearActiveLocationSelection();
    return;
  }

  const locId = localStorage.getItem(getBusinessLocationIdKey(businessId));
  const locScopeId = localStorage.getItem(getBusinessLocationScopeKey(businessId));

  if (locId) localStorage.setItem(LS_CURRENT_LOCATION_ID, String(locId));
  else localStorage.removeItem(LS_CURRENT_LOCATION_ID);

  if (locScopeId && locScopeId !== "all") {
    localStorage.setItem(LS_CURRENT_LOCATION_SCOPE_ID, String(locScopeId));
  } else {
    localStorage.removeItem(LS_CURRENT_LOCATION_SCOPE_ID);
  }
};

// P1+: access token în memorie, refresh token în cookie HttpOnly (backend).
// Nu mai folosim localStorage/sessionStorage pentru token-uri.
const clearTokens = () => {
  clearAccessToken();
};

function loadDemoAuthPayload() {
  return _getDemoAuthPayload();
}

function getSlugFromHost() {
  try {
    const hostname = window.location.hostname; // fără port
    if (!hostname) return null;

    // dev: <slug>.localhost
    if (hostname.endsWith(".localhost")) {
      const slug = hostname.split(".")[0];
      return slug || null;
    }

    // prod: <slug>.<domain>.<tld>
    if (hostname !== "localhost" && !hostname.startsWith("127.")) {
      const parts = hostname.split(".");
      if (parts.length >= 3) return parts[0] || null;
    }
  } catch (e) {
    // ignore
  }
  return null;
}

export function AuthProvider({ children }) {
  const [status, setStatus] = useState("unknown"); // 'unknown' | 'authenticated' | 'unauthenticated'
  const [user, setUser] = useState(null);
  const [roles, setRoles] = useState([]); // listă de label-uri ("Super Administrator", "Business Owner", etc.)
  const [roleDetails, setRoleDetails] = useState(null); // business_roles + location_roles
  const [businesses, setBusinesses] = useState([]);
  const [returnTo, setReturnTo] = useState(null);
  const [currentBusinessId, setCurrentBusinessId] = useState(
    localStorage.getItem(LS_CURRENT_BUSINESS_ID) || null
  );

  // util pentru UI: așteptăm bootstrap (refresh-cookie -> access -> /me)
  const [loading, setLoading] = useState(true);

  // alege business-ul inițial ținând cont de subdomeniu + ce era în localStorage
  const pickInitialBusinessId = (bizList) => {
    if (!bizList || bizList.length === 0) return null;

    const slugFromHost = getSlugFromHost();

    // 1) Dacă suntem pe subdomeniu (prod sau *.localhost),
    // MUST match business slug. Altfel, NU alegem alt business (anti cross-business).
    if (slugFromHost) {
      const match = bizList.find(
        (b) => String(b.slug || "").toLowerCase() === String(slugFromHost).toLowerCase()
      );
      return match ? String(match.id) : null;
    }

    // 2) Dacă NU avem subdomeniu (ex: localhost simplu), putem folosi business salvat
    const stored = localStorage.getItem(LS_CURRENT_BUSINESS_ID);
    if (stored && bizList.some((b) => String(b.id) === stored)) {
      return stored;
    }

    // 3) fallback: primul business
    return String(bizList[0].id);
  };

  const setAuth = (payload, opts = {}) => {
    const bizList = payload.businesses || [];
    const userPayload = payload.user || null;
    const rolesPayload = payload.roles || [];
    const roleDetailsPayload = payload.roleDetails || userPayload?.role_details || null;
    const previousBusinessId =
      currentBusinessId || localStorage.getItem(LS_CURRENT_BUSINESS_ID) || null;

    persistLocationSelection(previousBusinessId);

    setUser(userPayload);
    setRoles(rolesPayload);
    setRoleDetails(roleDetailsPayload);
    setBusinesses(bizList);
    setStatus("authenticated");

    if (bizList.length) {
      const preferredBusinessId = opts?.preferredBusinessId ? String(opts.preferredBusinessId) : null;
      const slugFromHost = getSlugFromHost();

      const chosenId = (!slugFromHost && preferredBusinessId && bizList.some((b) => String(b.id) === preferredBusinessId))
        ? preferredBusinessId
        : pickInitialBusinessId(bizList);

      setCurrentBusinessId(chosenId);

      if (chosenId) {
        localStorage.setItem(LS_CURRENT_BUSINESS_ID, chosenId);
        restoreLocationSelection(chosenId);

        // IMPORTANT: pe localhost/IP avem nevoie de slug în storage pentru header X-Business-Subdomain
        const chosenBiz = bizList.find((b) => String(b.id) === String(chosenId));
        if (chosenBiz?.slug) {
          localStorage.setItem(LS_CURRENT_BUSINESS_SLUG, String(chosenBiz.slug));
        }
      } else {
        // IMPORTANT: dacă suntem pe subdomeniu și nu există match în businesses,
        // nu păstrăm business_id vechi din storage (altfel sari între afaceri).
        localStorage.removeItem(LS_CURRENT_BUSINESS_ID);
        localStorage.removeItem(LS_CURRENT_BUSINESS_SLUG);
        clearActiveLocationSelection();
      }
    } else {
      setCurrentBusinessId(null);
      localStorage.removeItem(LS_CURRENT_BUSINESS_ID);
      localStorage.removeItem(LS_CURRENT_BUSINESS_SLUG);
      clearActiveLocationSelection();
    }
  };

  const setBusiness = (bid) => {
    const v = bid ? String(bid) : null;

    const slugFromHost = getSlugFromHost();
    if (slugFromHost) {
      // dacă suntem pe subdomeniu, nu permitem setarea unui business diferit
      const match = (businesses || []).find(
        (b) => String(b.slug || "").toLowerCase() === String(slugFromHost).toLowerCase()
      );
      const allowedId = match ? String(match.id) : null;

      if (!allowedId || (v && v !== allowedId)) {
        // blochează schimbarea cross-business
        setCurrentBusinessId(allowedId);
        if (allowedId) {
          localStorage.setItem(LS_CURRENT_BUSINESS_ID, allowedId);
          restoreLocationSelection(allowedId);
        } else {
          localStorage.removeItem(LS_CURRENT_BUSINESS_ID);
          clearActiveLocationSelection();
        }
        return;
      }
    }

    const previousBusinessId =
      currentBusinessId || localStorage.getItem(LS_CURRENT_BUSINESS_ID) || null;
    persistLocationSelection(previousBusinessId);

    setCurrentBusinessId(v);
    if (v) {
      localStorage.setItem(LS_CURRENT_BUSINESS_ID, v);
      restoreLocationSelection(v);
      const chosenBiz = (businesses || []).find((b) => String(b.id) === String(v));
      if (chosenBiz?.slug) localStorage.setItem(LS_CURRENT_BUSINESS_SLUG, String(chosenBiz.slug));
    } else {
      localStorage.removeItem(LS_CURRENT_BUSINESS_ID);
      localStorage.removeItem(LS_CURRENT_BUSINESS_SLUG);
      clearActiveLocationSelection();
    }
  };

  const clearAuth = () => {
    const previousBusinessId =
      currentBusinessId || localStorage.getItem(LS_CURRENT_BUSINESS_ID) || null;

    persistLocationSelection(previousBusinessId);
    setUser(null);
    setRoles([]);
    setRoleDetails(null);
    setBusinesses([]);
    setCurrentBusinessId(null);
    localStorage.removeItem(LS_CURRENT_BUSINESS_ID);
    localStorage.removeItem(LS_CURRENT_BUSINESS_SLUG);
    clearActiveLocationSelection();
    setStatus("unauthenticated");
  };

  // bootstrap (P1+): refresh-cookie -> access in memory -> /me -> setAuth
  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        if (isDemoMode) {
          const demoAuth = loadDemoAuthPayload();
          if (!alive) return;
          setAuth(demoAuth);
          return;
        }

        // 1) ia access token nou din refresh-cookie (HttpOnly)
        await refreshAccessToken();

        // 2) după ce ai access, ia profilul
        const res = await api.get("/auth/me");
        if (!alive) return;

        const me = res?.data || {};
        const userPayload = me?.user || me;
        const rolesPayload = me?.roles || [];
        const businessesPayload = me?.businesses || [];
        const roleDetailsPayload = me?.roleDetails || userPayload?.role_details || null;

        setAuth({
          user: userPayload,
          roles: rolesPayload,
          businesses: businessesPayload,
          roleDetails: roleDetailsPayload,
        });
      } catch (e) {
        if (!alive) return;
        clearTokens();
        clearAuth();
      } finally {
        if (!alive) return;
        setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // apelată după login (vezi LoginPage)
  const loginSuccess = ({ access, me }) => {
    if (access) setAccessToken(access);

    const userPayload = me?.user || me;
    const rolesPayload = me?.roles || [];
    const businessesPayload = me?.businesses || [];
    const roleDetailsPayload = me?.roleDetails || userPayload?.role_details || null;

    setAuth({
      user: userPayload,
      roles: rolesPayload,
      businesses: businessesPayload,
      roleDetails: roleDetailsPayload,
    });
  };

  const logout = async () => {
    if (isDemoMode) {
      clearTokens();
      const demoAuth = loadDemoAuthPayload();
      setAuth(demoAuth, { preferredBusinessId: currentBusinessId });
      return;
    }

    try {
      await apiLogout();
    } finally {
      clearTokens();
      clearAuth();
    }
  };

  // ===== Refresh /auth/me (useful after Stripe checkout in another tab) =====
  const refreshMe = useCallback(async ({ silent = true } = {}) => {
    try {
      if (isDemoMode) {
        const demoAuth = loadDemoAuthPayload();
        setAuth(demoAuth, { preferredBusinessId: currentBusinessId });
        return demoAuth;
      }

      const res = await api.get("/auth/me");
      const me = res?.data || {};
      const userPayload = me?.user || me;
      const rolesPayload = me?.roles || [];
      const businessesPayload = me?.businesses || [];
      const roleDetailsPayload = me?.roleDetails || userPayload?.role_details || null;

      setAuth(
        {
          user: userPayload,
          roles: rolesPayload,
          businesses: businessesPayload,
          roleDetails: roleDetailsPayload,
        },
        { preferredBusinessId: currentBusinessId }
      );

      return me;
    } catch (e) {
      // If refresh fails due to auth, clear session.
      const st = e?.response?.status;
      if (st === 401 || st === 403) {
        clearTokens();
        clearAuth();
      }
      if (!silent) throw e;
      return null;
    }
  }, [currentBusinessId]);

  // Refresh on focus/visibility (throttled) so the app notices payments done in /billing.
  const lastMeRefreshAtRef = useRef(0);
  useEffect(() => {
    const maybeRefresh = () => {
      const now = Date.now();
      if (now - lastMeRefreshAtRef.current < 15000) return;
      lastMeRefreshAtRef.current = now;
      refreshMe();
    };

    const onFocus = () => maybeRefresh();
    const onVis = () => {
      if (document.visibilityState === "visible") maybeRefresh();
    };

    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [refreshMe]);

  // Cross-tab signal from /billing (site) after successful checkout
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === "billing_success") {
        refreshMe();
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [refreshMe]);

  // --------- Derivăm permisiunile pentru UI în funcție de roluri ---------

  const permissions = useMemo(() => {
    const currentBizId = currentBusinessId ? String(currentBusinessId) : null;

    const currentBiz = (businesses || []).find((b) => String(b.id) === String(currentBizId));

    const currentBusinessFeatures = currentBiz?.features || null;

    const hasFeature = (key) => {
      const k = String(key || "").toLowerCase().trim();
      const canonical = k === "analytics" ? "stats" : k;
      if (!currentBusinessFeatures) return true; // compat: dacă backend nu trimite, nu blocăm UI
      return Boolean(
        currentBusinessFeatures[canonical] ??
          (canonical === "stats" ? currentBusinessFeatures.analytics : undefined)
      );
    };

    const isSuperAdmin = roles?.includes("Super Administrator");

    const businessRoles = roleDetails?.business_roles || [];
    const locationRoles = roleDetails?.location_roles || [];

    const businessRolesCurrentBusiness = businessRoles.filter(
      (br) => String(br.business_id) === currentBizId
    );

    const isOwnerCurrentBusiness =
      isSuperAdmin || businessRolesCurrentBusiness.some((br) => br.role === "owner");

    const locationRolesCurrentBusiness = locationRoles.filter(
      (lr) => String(lr.business_id) === currentBizId
    );

    // Roluri "efective" = location roles OR business-level roles (când userul încă n-are location)
    const isDirectorCurrentBusiness =
      locationRolesCurrentBusiness.some((lr) => lr.role === "director") ||
      businessRolesCurrentBusiness.some((br) => br.role === "director");

    const isReceptionCurrentBusiness =
      locationRolesCurrentBusiness.some((lr) => lr.role === "reception") ||
      businessRolesCurrentBusiness.some((br) => br.role === "reception");

    const isEmployeeCurrentBusiness =
      locationRolesCurrentBusiness.some((lr) => lr.role === "employee") ||
      businessRolesCurrentBusiness.some((br) => br.role === "employee");

    const hasAnyLocationRoleCurrentBusiness = locationRolesCurrentBusiness.length > 0;

    const hasAnyRoleCurrentBusiness =
      businessRolesCurrentBusiness.length > 0 || hasAnyLocationRoleCurrentBusiness;

    const canManageStaff = isOwnerCurrentBusiness;
    const canManageBusinessSettings = isOwnerCurrentBusiness;

    const canSeeAppointments =
      (isSuperAdmin || hasAnyRoleCurrentBusiness) && hasFeature("appointments");

    // ✅ Plan gating
    const canSeeDashboardRole = isSuperAdmin || isOwnerCurrentBusiness || isDirectorCurrentBusiness;

    // "stats" is the feature flag used for dashboards/analytics/KPI cards
    const canSeeStats = canSeeDashboardRole && hasFeature("stats");
    const canSeeDashboard = canSeeStats;

    const canSeeClientsRole =
      isSuperAdmin || isOwnerCurrentBusiness || isDirectorCurrentBusiness || isReceptionCurrentBusiness;
    const canSeeClients = canSeeClientsRole && hasFeature("clients");

    // Inventory (stocuri)
    const canSeeInventoryRole = canSeeClientsRole;
    const canSeeInventory = canSeeInventoryRole && hasFeature("inventory");

    const canSeeStaffRole = isSuperAdmin || isOwnerCurrentBusiness || isDirectorCurrentBusiness;
    const canSeeStaff = canSeeStaffRole && hasFeature("staff");

    const canSeeServicesRole = isSuperAdmin || isOwnerCurrentBusiness || isDirectorCurrentBusiness;
    const canSeeServices = canSeeServicesRole && hasFeature("services");

    // SMS (add-on)
    // Pentru meniuri/pagini: Owner/Director/Recepție. (Angajatul "pur" rămâne doar cu Calendar.)
    const canSeeSmsRole = canSeeClientsRole;
    const canSeeSms = canSeeSmsRole && hasFeature("sms");

    // Campaniile SMS sunt (deocamdată) parte din pachetul SMS.
    // Dacă vrei un add-on separat mai târziu, îl separăm în feature: "sms_campaigns".
    const canSeeSmsCampaigns = canSeeSmsRole && hasFeature("sms");

    // Program de lucru (working hours): userul trebuie să poată vedea și modifica
    // dacă are ORICE rol pe una din locațiile business-ului (inclusiv employee)
    const canSeeWorkingHours = isSuperAdmin || isOwnerCurrentBusiness || hasAnyLocationRoleCurrentBusiness;

    const canSeeOwnAppointments = isSuperAdmin || isOwnerCurrentBusiness || hasAnyLocationRoleCurrentBusiness;

    const isPureEmployee =
      !isSuperAdmin &&
      !isOwnerCurrentBusiness &&
      !isDirectorCurrentBusiness &&
      !isReceptionCurrentBusiness &&
      isEmployeeCurrentBusiness;

    return {
      isSuperAdmin,
      isOwnerCurrentBusiness,
      isDirectorCurrentBusiness,
      isReceptionCurrentBusiness,
      isEmployeeCurrentBusiness,
      isPureEmployee,
      hasAnyLocationRoleCurrentBusiness,
      locationRolesCurrentBusiness,
      businessRoles,
      locationRoles,

      currentBusinessFeatures,
      hasFeature,

      canManageStaff,
      canManageBusinessSettings,
      canSeeAppointments,
      canSeeDashboard,
      canSeeStats,
      canSeeClients,
      canSeeStaff,
      canSeeServices,
      canSeeSms,
      canSeeSmsCampaigns,
      canSeeInventory,
      canSeeWorkingHours,
      canSeeOwnAppointments,
    };
  }, [roles, roleDetails, currentBusinessId, businesses]);

  // Pentru owner: dacă business-ul are exact o singură sucursală,
  // o folosim ca default atât pentru scope, cât și pentru acțiunile operaționale.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!currentBusinessId || !roleDetails) return;

    const isSuperAdmin = roles?.includes("Super Administrator");
    const businessRoles = roleDetails?.business_roles || [];
    const isOwnerCurrentBusiness =
      isSuperAdmin ||
      businessRoles.some(
        (br) => String(br.business_id) === String(currentBusinessId) && br.role === "owner"
      );

    if (!isOwnerCurrentBusiness) return;

    let cancelled = false;

    const syncOwnerDefaultLocation = async () => {
      try {
        const { data } = await api.get("/b/locations", {
          headers: { "X-Business-Id": String(currentBusinessId) },
        });

        if (cancelled) return;

        const locations = Array.isArray(data)
          ? data
          : Array.isArray(data?.results)
            ? data.results
            : [];

        if (locations.length !== 1 || locations[0]?.id == null) return;

        const onlyId = String(locations[0].id);

        if (window.localStorage.getItem(LS_CURRENT_LOCATION_ID) !== onlyId) {
          window.localStorage.setItem(LS_CURRENT_LOCATION_ID, onlyId);
        }

        if (window.localStorage.getItem(LS_CURRENT_LOCATION_SCOPE_ID) !== onlyId) {
          window.localStorage.setItem(LS_CURRENT_LOCATION_SCOPE_ID, onlyId);
        }

        // Persistăm și sub cheia business-specifică, altfel restoreLocationSelection
        // o șterge la fiecare reload și apare race condition cu async-ul acestui effect.
        const bizLocKey = getBusinessLocationIdKey(currentBusinessId);
        if (window.localStorage.getItem(bizLocKey) !== onlyId) {
          window.localStorage.setItem(bizLocKey, onlyId);
        }
        const bizScopeKey = getBusinessLocationScopeKey(currentBusinessId);
        if (window.localStorage.getItem(bizScopeKey) !== onlyId) {
          window.localStorage.setItem(bizScopeKey, onlyId);
        }
      } catch (e) {
        if (!cancelled) {
          console.error("Nu am putut determina locația implicită pentru owner:", e?.response?.data || e);
        }
      }
    };

    syncOwnerDefaultLocation();

    return () => {
      cancelled = true;
    };
  }, [currentBusinessId, roleDetails, roles]);

  // Pentru non-owner, asigurăm un current_location_id valid (altfel nu poți schimba programul).
  // Dacă userul are 1 singură locație atribuită, o selectăm automat.
  // Dacă are mai multe, păstrăm ce există deja, dar îl forțăm să fie una "permisă".
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!currentBusinessId || !roleDetails) return;

    const isSuperAdmin = roles?.includes("Super Administrator");
    const businessRoles = roleDetails?.business_roles || [];
    const isOwnerCurrentBusiness =
      isSuperAdmin ||
      businessRoles.some(
        (br) => String(br.business_id) === String(currentBusinessId) && br.role === "owner"
      );

    // Owner-ul poate lucra cross-location, nu îi setăm current_location_id automat.
    if (isOwnerCurrentBusiness) return;

    const locRoles = (roleDetails?.location_roles || []).filter(
      (lr) => String(lr.business_id) === String(currentBusinessId)
    );
    const allowedLocationIds = Array.from(
      new Set(locRoles.map((lr) => lr.location_id).filter(Boolean))
    ).map((x) => Number(x));

    if (!allowedLocationIds.length) return;

    const rawCur = window.localStorage.getItem("current_location_id");
    const cur = rawCur ? Number(rawCur) : null;

    const hasCur = cur && allowedLocationIds.includes(cur);
    if (hasCur) return;

    // fallback: prima locație atribuită
    window.localStorage.setItem("current_location_id", String(allowedLocationIds[0]));
  }, [currentBusinessId, roleDetails, roles]);

  const value = useMemo(
    () => ({
      status,
      loading,
      user,
      roles,
      roleDetails,
      businesses,
      currentBusinessId,
      setCurrentBusinessId: setBusiness,
      returnTo,
      setReturnTo,
      setAuth,
      clearAuth,
      loginSuccess,
      logout,
      refreshMe,
      permissions,
    }),
    [
      status,
      loading,
      user,
      roles,
      roleDetails,
      businesses,
      currentBusinessId,
      returnTo,
      permissions,
    ]
  );

  // Înregistrare push token FCM după autentificare (doar pe native)
  usePushNotifications();

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
};
